import { toast as sonnerToast } from 'sonner';
import { components } from '@/lib/design-tokens';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  title?: string;
  description?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Custom hook for displaying toast notifications
 * Wraps sonner's toast functionality with CareCal design system
 */
export function useToast() {
  const showToast = (variant: ToastVariant, message: string, options?: ToastOptions) => {
    const duration = options?.duration ?? components.toast.duration;

    const toastOptions = {
      description: options?.description,
      duration,
      action: options?.action
        ? {
            label: options.action.label,
            onClick: options.action.onClick,
          }
        : undefined,
    };

    switch (variant) {
      case 'success':
        return sonnerToast.success(message, toastOptions);
      case 'error':
        return sonnerToast.error(message, toastOptions);
      case 'info':
        return sonnerToast.info(message, toastOptions);
      case 'warning':
        return sonnerToast.warning(message, toastOptions);
      default:
        return sonnerToast(message, toastOptions);
    }
  };

  return {
    toast: showToast,
    success: (message: string, options?: ToastOptions) => showToast('success', message, options),
    error: (message: string, options?: ToastOptions) => showToast('error', message, options),
    info: (message: string, options?: ToastOptions) => showToast('info', message, options),
    warning: (message: string, options?: ToastOptions) => showToast('warning', message, options),
    dismiss: (toastId?: string | number) => sonnerToast.dismiss(toastId),
  };
}

// Type for the return value of useToast hook
export type UseToastReturn = ReturnType<typeof useToast>;
